import { useCallback, useEffect, useState } from 'react';

import type { MemberCard, MemberRow, PauseSpan } from '@contracts/index';

import { ApiError, api } from '../api';
import { MONTHS_SHORT, monthIn, monthOf, plural, voicePartName, words } from '../format';
import { useBackKey } from '../backKey';
import Ask from './Ask';
import Avatar, { AVATAR_CARD } from './Avatar';
import ProfileFields from './ProfileFields';
import Sheet from './Sheet';
import { Failed, Settling, Skeleton } from './States';
import type { Report } from './UndoBar';

/**
 * **One person's card, from `legacy/frontend/src/screens/PersonScreen.tsx` — a region port,
 * built up task by task** (`docs/specs/features/F-016/plan.md` T10, T11;
 * `docs/specs/features/F-017/plan.md` T7, T10, T12).
 *
 * What is here is the card as it is read: the doc comment (`:13-31`), `card` and `failure`
 * (`:63-65`), `load` and its effect (`:75-89`), the key (`:91-93`), the skeleton and the failure
 * (`:142-148`), the head with the face at `AVATAR_CARD`, the name, the pill and the line under
 * them (`:150-177`), the pause note (`:179-191`), the two figures (`:154`, `:193-217`) — F-016
 * T10's — then «Год по месяцам» with `MonthChart` and the gap line (`:219-227`), the card's line
 * (`:561-571`), and `inAPause`, `monthKey`, `MonthChart` and `gapLine` (`:573-658`) — F-016
 * T11's. `gapLine` reads the last span of all, not the last inside the chart's twelve months, and
 * names its months without a year (**`FND-513`, `KEEP`**); `monthKey` keys a span in the reader's
 * zone against the server's month keys, as the old one does.
 *
 * F-017 T7 brought the keeper's half as far as its four direct acts: the props with their doc
 * comments (`:32-62`), `busy`, `sheet` and `asking` (`:66-70`), `act` and `reversibly`
 * (`:103-140`), `deputy` (`:152-153`), «Что можно сделать» and everything under it
 * (`:228-440`), and the `Report` import that types `onDone`. The gates are the old ones with
 * their oddities: a deputy is offered the demotion and the pause on his own card
 * (**`FND-532`**), a person without Telegram the two functions (**`FND-533`**), the sermons
 * function is gated here alone (**`FND-534`**), and the deputy's hint says «главный регент»
 * (**`FND-535`**) — all four `KEEP`.
 *
 * F-017 T10 brought the three sheets: `note` and `typed` (`:71-73`), `closeSheet` (`:95-101`),
 * the edit `Sheet` with `ProfileFields` (`:443-474`), `PauseSheet` and `DeleteSheet` in the
 * fragment (`:517-556`) and as functions (`:660-817`), the imports `ProfileFields` and `Sheet`,
 * and `onGone`'s destructuring (`:38`), which T7 left in the type alone because its one reader
 * is `DeleteSheet` (`:547`). They are the old ones with theirs: the pause note has no length
 * limit, so past 200 characters the foot says «Ошибка 422» (**`FND-529`**); neither the pause
 * nor the profile raises a report (**`FND-531`**); the pause sheet names one function when both
 * are held (**`FND-538`**); the edit sends the stored avatar and birthday back as they are
 * (**`FND-541`**); «Изменить имя и партию» changes the sex as well (**`FND-544`**); and the
 * deletion forgives spaces at both ends, where `api.ts`'s comment says «trailing»
 * (**`FND-543`**) — all `KEEP`.
 *
 * F-017 T12 brought the last of it: the seniority `Ask` (`:476-515`) and its import. It is the
 * old one with its two: closing it leaves a refusal in `failure`, so the block's line draws the
 * sentence once the question is gone (**`FND-536`**), and the handover's answer, the whole
 * roster, is never read — the card leaves by `onBack` and the hub reads the roster again
 * (**`FND-540`**) — both `KEEP`. Nothing on the old card is absent now.
 *
 * `subLine` is **`cardLine`** here (`:562`, called at `:175`): the roster has a `subLine` of its
 * own that says different things about the same person, and neither may be importable in the
 * other's place — **`FND-509`, `KEEP`**. The card's line has no tenure (**`FND-508`, `KEEP`**).
 *
 * Carried as they stand: the doc comment, which still speaks of «the three buttons»; the load
 * failure's text, stored and read by nothing (`:80-83`, **`FND-505`, `KEEP`**);
 * `Failed subject="members"`, so a person's page says «Состав не пришёл», for a `404` as for a
 * lost connection (`:145`, **`FND-506`, `KEEP`**); the year's share beside the row's 30-day one,
 * both the server's and never reconciled (`:154`, **`FND-517`, `KEEP`**); and
 * `MemberCard.attended`, read by nothing (**`FND-518`, `KEEP`**).
 *
 * Their `:3` takes the server shapes from `../api`; here they come from `@contracts/index`
 * (ADR-0011). The regions of the old file this one reproduces line for line are on
 * `scripts/ported-partial.txt`.
 */

/**
 * One person, and everything that can be done to them.
 *
 * The roster row became a door on 2026-08-27 and this is what is behind it. The
 * three acts that used to live in the «…» — take somebody out, bring them back,
 * and now erase them — moved here, because a menu inside a list row has space
 * for a verb and none for what the verb costs. «Пауза — обратимая: человек и
 * его отметки остаются. Удаление стирает и то и другое» is a sentence that has
 * to stand somewhere, and there is nowhere in a roster row to stand it.
 *
 * Open to the whole choir, and that is the point of the split: a singer gets the
 * figures and the year, which are facts about a person the choir already knows,
 * and none of the three buttons. What is the regent's own — why somebody is
 * away — is withheld by the server field by field rather than by closing the
 * screen, so a singer's copy is the same screen with less on it.
 *
 * No `h1`. The name stands beside the face at the top, and a screen title over
 * it would be the same name printed twice.
 */
export default function PersonScreen({
  memberId,
  canManage,
  isHead,
  otherRegents,
  onBack,
  onGone,
  onDone,
}: {
  memberId: number;
  /** Whether this reader keeps the roster: the head, or a deputy he trusts. */
  canManage: boolean;
  /** The head regent alone may erase somebody, and only he is offered the entry. */
  isHead: boolean;
  /**
   * How many active regents the choir has **besides this one**, counted on
   * «Хор» where the roster is in hand. It decides one thing: the role cannot be
   * taken off the last regent standing. «Besides this one» rather than the
   * total, because the button beside it changes whether this person is a
   * regent — a total would go stale on the very tap it guards.
   */
  otherRegents: number;
  onBack: () => void;
  /**
   * The person has been erased. The screen has nothing left to draw, so «Хор»
   * takes over — and it is handed the new invite code, because the deletion
   * rotated it and somebody has to be told.
   */
  onGone: (inviteCode: string) => void;
  onDone: Report;
}) {
  const [card, setCard] = useState<MemberCard | null>(null);
  /** The refusal and where it belongs: `screen` for loading, `sheet` for a tap. */
  const [failure, setFailure] = useState<{ at: 'screen' | 'sheet'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  /** Which sheet is up. All three hold a field, which is why all three are sheets. */
  const [sheet, setSheet] = useState<null | 'pause' | 'delete' | 'edit'>(null);
  /** Whether the seniority is being asked about, which is the one question here. */
  const [asking, setAsking] = useState(false);
  /** What has been typed into whichever sheet is open. */
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');

  const load = useCallback(async () => {
    try {
      setCard(await api.memberCard(memberId));
      setFailure(null);
    } catch (error) {
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить человека',
      });
    }
  }, [memberId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The key, which the sheet takes off this screen for as long as it is up:
  // `onBackButton` calls the innermost holder and the sheet registers later.
  useBackKey(onBack);

  function closeSheet(): void {
    setSheet(null);
    setAsking(false);
    setNote('');
    setTyped('');
    setFailure(null);
  }

  /** Puts the row that came back on the screen, or the refusal where the tap was. */
  async function act(action: () => Promise<MemberRow>): Promise<MemberRow | null> {
    setBusy(true);
    setFailure(null);
    try {
      const updated = await action();
      setCard((current) => (current === null ? current : { ...current, member: updated }));
      // The stretches and the chart change with the standing — a pause opens one
      // and coming back closes it — so the card is re-read rather than patched.
      void load();
      return updated;
    } catch (error) {
      setFailure({
        at: 'sheet',
        text: error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
      });
      return null;
    } finally {
      setBusy(false);
    }
  }

  /**
   * A change that can be taken back, done at once and reported to the bar.
   *
   * The undo is the same call in the opposite direction — the same screen, the
   * same request, no endpoint written for going back — which is what makes
   * these four reversible in the sense the container rule means.
   */
  async function reversibly(
    change: () => Promise<MemberRow>,
    said: (row: MemberRow) => string,
    back: () => Promise<MemberRow>,
  ): Promise<void> {
    const updated = await act(change);
    if (updated === null) return;
    onDone(said(updated), () => void act(back));
  }

  if (card === null) {
    return (
      <>
        {failure === null ? <Skeleton /> : <Failed subject="members" onRetry={() => void load()} />}
      </>
    );
  }

  const person = card.member;
  const away = person.standing === 'paused';
  /** Another regent: the two things only the head may do are offered on one. */
  const deputy = isHead && !away && person.role === 'regent' && !person.is_head_regent;
  const share = card.rate === null ? '—' : `${Math.round(card.rate * 100)}%`;

  return (
    <>
      <Settling>
        <div className="person-head">
          <Avatar
            eyes={person.avatar_eyes}
            color={person.avatar_color}
            rate={person.attendance_rate_30d}
            size={AVATAR_CARD}
            away={away}
            mine={person.is_me}
          />
          <span className="person-body">
            <span className="person-line">
              <span className="person-name">{person.display_name}</span>
              {/* Not accented, and that is the whole of what this pill says: a
                  pause is neither an event nor an achievement. */}
              {away && <span className="answer answer--away">в паузе</span>}
            </span>
            <span className="live-sub live-sub--wrap">{cardLine(person)}</span>
          </span>
        </div>

        {/* The regent's own note, and only ever in his own copy: the server sends
            it to nobody else, so this block simply has nothing to draw for a
            singer — and nothing to draw for the person it is about, who hears the
            reason from the regent rather than from the app. */}
        {away && person.pause_note !== null && (
          <section className="card">
            <span className="label">
              Пауза{person.paused_at === null ? '' : ` с ${monthOf(new Date(person.paused_at).getMonth() + 1)}`}
            </span>
            <p className="card-line">{person.pause_note}</p>
            <p className="hint">Видно только регенту и заместителям.</p>
          </section>
        )}

        {/* Two figures and no third. The share is what the year says about the
            person; the count is what the choir has of them. */}
        <div className="tiles">
          <section className="card">
            <p className={away ? 'figure figure--stat figure--away' : 'figure figure--stat'}>
              {share}
            </p>
            <p className="tile-caption">
              {away ? 'посещаемость до паузы' : 'посещаемость за год'}
            </p>
          </section>
          <section className="card">
            <p className={away ? 'figure figure--stat figure--away' : 'figure figure--stat'}>
              {card.total}
            </p>
            {/* The genitive plural is right for five and wrong for the other
                four, and the card of a choir with two rehearsals behind it said
                «2 спевок». The endpoint counts rehearsals only - a service in
                the figure would make «посещаемость» beside it a different
                sentence - so the kind is not a question here. */}
            <p className="tile-caption">
              {plural(card.total, words('rehearsal').counted)} в истории
            </p>
          </section>
        </div>

        <section className="block">
          <span className="label">Год по месяцам</span>
          <MonthChart months={card.months} pauses={card.pauses} />
          {/* What the gap in the chart is, said in words: a picture that has to be
              interpreted is a picture that gets interpreted wrongly. Drawn only
              where there is a gap — a year somebody sang the whole of needs no
              explaining. */}
          {gapLine(card) !== null && <p className="hint">{gapLine(card)}</p>}
        </section>

        {/* Everything a regent does to a person, in one column and in one order:
            the ordinary at the top, the irreversible at the bottom. It lived in
            the «…» of a roster row until 2026-08-27, where a verb had room and
            what the verb costs had none — «Пауза — обратимая… Удаление стирает и
            то и другое» is a sentence with nowhere to stand inside a 46px row.

            Everything reversible happens on the tap with «Отменить» under the
            screen for five seconds, which is the container rule of 2026-08-21;
            the two with a field in them open as sheets, and the one that hands
            the seniority on asks, because only the person receiving it can give
            it back. */}
        {canManage && (
          <div className="block">
            <span className="label">Что можно сделать</span>

            {away && (
              <button
                className="btn btn--primary"
                disabled={busy}
                onClick={() =>
                  void act(() => api.resumeMember(person.member_id)).then((updated) => {
                    if (updated === null) return;
                    onDone(`${updated.display_name} снова в хоре`, () => {
                      void act(() => api.pauseMember(person.member_id, person.pause_note));
                    });
                  })
                }
              >
                Вернуть в хор
              </button>
            )}

            {/* The two delegated functions. Handing one on does not take it off
                the regent — «не поручил — делает регент; поручил — делает и он»,
                the owner's rule of 2026-08-24 — so the verb is «Поручить» and
                never «Передать». At most one holder per choir, so giving it to
                somebody takes it off whoever had it, in the same transaction.

                They stay on the head regent's own card, against this revision of
                the handoff and with DESIGN.md of 2026-08-24: his row loses the
                role and the removal and keeps the rest, and the server has never
                refused him a function.

                The label names the work with a verb, not the subject with a noun.
                «Поручить проповеди» read as «назначить его проповедовать» — and
                the button is only on a brother's card, that is on the card of
                somebody who really does preach, so the wrong reading was the more
                plausible of the two; it means the right to SET the rota for the
                others. One verb across all three handovers, «Доверить» gone with
                it: to entrust and to assign are one operation here, and two words
                made a reader look for a difference that is not there. The words
                come from these buttons' own toasts, which have named the work
                correctly all along. */}
            {!away && (
              <button
                className="btn btn--outline"
                disabled={busy}
                onClick={() =>
                  void reversibly(
                    () => api.setMemberFunction(person.member_id, 'program', !person.is_program_admin),
                    (updated) =>
                      updated.is_program_admin
                        ? `${updated.display_name} ведёт программу`
                        : 'Программу снова ведёт регент',
                    () =>
                      api.setMemberFunction(person.member_id, 'program', person.is_program_admin),
                  )
                }
              >
                {person.is_program_admin ? 'Забрать программу' : 'Поручить вести программу'}
              </button>
            )}
            {/* Only on a brother's card: the rota is a queue of who preaches, and
                the person who keeps it is one of them. */}
            {!away && person.is_brother === true && (
              <button
                className="btn btn--outline"
                disabled={busy}
                onClick={() =>
                  void reversibly(
                    () => api.setMemberFunction(person.member_id, 'sermons', !person.is_sermon_admin),
                    (updated) =>
                      updated.is_sermon_admin
                        ? `${updated.display_name} ставит проповеди`
                        : 'Проповеди снова ставит регент',
                    () =>
                      api.setMemberFunction(person.member_id, 'sermons', person.is_sermon_admin),
                  )
                }
              >
                {person.is_sermon_admin ? 'Забрать проповеди' : 'Поручить ставить проповеди'}
              </button>
            )}

            {/* Not on a paused card: `POST /members/{id}/profile` has refused
                anybody who is not in the choir since long before this work, so
                the button would be certain to fail. A control that is refused
                every time is worse than one that is absent — this app's own rule
                about a form nobody can submit. */}
            {!away && (
              <button className="btn btn--outline" disabled={busy} onClick={() => setSheet('edit')}>
                Изменить имя и партию
              </button>
            )}

            {/* A second regent is the only insurance against a lost phone, and one
                has to be appointed while the first one still can. The handoff of
                this revision leaves the entry out of its table; it stays, on the
                owner's word — without it a choir has no way to make a second
                regent at all.

                Refused on the head regent by anybody including himself, and on
                the last regent left standing: both are the choir with nobody who
                can open a rehearsal, reached by two doors.

                And not drawn at all for somebody the regent entered by hand: a
                regent opens the rehearsal, holds the code and keeps the roster,
                all of it inside an app they cannot open. The server refuses it,
                and a button certain to be refused is a promise the screen has no
                business making — the same reason a rehearsal past its edit window
                draws no toggles. The line under the block says why instead. */}
            {!away &&
              person.has_telegram &&
              !person.is_head_regent &&
              !(person.role === 'regent' && otherRegents === 0) && (
              <button
                className="btn btn--outline"
                disabled={busy}
                onClick={() =>
                  void reversibly(
                    () =>
                      api.setMemberRole(
                        person.member_id,
                        person.role === 'regent' ? 'singer' : 'regent',
                      ),
                    (updated) =>
                      updated.role === 'regent'
                        ? `${updated.display_name} теперь регент`
                        : `${updated.display_name} больше не регент`,
                    () => api.setMemberRole(person.member_id, person.role),
                  )
                }
              >
                {person.role === 'regent' ? 'Снять роль регента' : 'Сделать регентом'}
              </button>
            )}

            {/* The privilege, offered only on another regent's card: people given
                to somebody who cannot open a rehearsal is a permission that does
                nothing until he is made a regent. */}
            {deputy && (
              <button
                className="btn btn--outline"
                disabled={busy}
                onClick={() =>
                  void reversibly(
                    () => api.setRosterAdmin(person.member_id, !person.is_roster_admin),
                    (updated) =>
                      updated.is_roster_admin
                        ? `${updated.display_name} ведёт состав хора`
                        : `${updated.display_name} больше не ведёт состав`,
                    () => api.setRosterAdmin(person.member_id, person.is_roster_admin),
                  )
                }
              >
                {person.is_roster_admin ? 'Забрать состав хора' : 'Поручить вести состав хора'}
              </button>
            )}
            {/* The one way out of the seniority, and the only thing here that is
                asked about: nothing takes it back except the new head, who may
                not want to. Not the danger colour — nobody is losing the choir. */}
            {deputy && (
              <button className="btn btn--outline" disabled={busy} onClick={() => setAsking(true)}>
                Передать старшинство
              </button>
            )}

            {!away && !person.is_head_regent && (
              <button className="btn btn--outline" disabled={busy} onClick={() => setSheet('pause')}>
                Поставить на паузу
              </button>
            )}
            {isHead && !person.is_head_regent && (
              <button
                className="btn btn--outline btn--danger-quiet"
                disabled={busy}
                onClick={() => setSheet('delete')}
              >
                Удалить из хора
              </button>
            )}

            {failure !== null && failure.at === 'sheet' && sheet === null && !asking && (
              <p className="failure">{failure.text}</p>
            )}
            {/* Said here rather than beside a greyed-out button: the missing
                control is not a state of this screen, it is a fact about the
                person, and the block's own closing line is where this screen
                already explains what its verbs cost. */}
            {!person.has_telegram && (
              <p className="hint">
                У этого человека нет Telegram: он не заходит в приложение, бот ему не пишет и
                регентом он быть не может. Отмечает его регент — с панели и в закрытой встрече.
              </p>
            )}
            <p className="hint">
              {isHead
                ? 'Пауза — обратимая: человек и его отметки остаются. Удаление стирает и то и другое.'
                : 'Удалить человека может только главный регент.'}
            </p>
          </div>
        )}
      </Settling>

      {/* The form is a sheet because it has a name field in it, and a name
          field means the keyboard: unfolded inside a screen it went under the
          keys together with «Сохранить». Neither the avatar nor the birthday is
          in it — both are the person's own, so the stored values ride through
          untouched and the server refuses a birthday that differs. */}
      <Sheet
        open={sheet === 'edit'}
        label={`Имя и партия · ${person.display_name}`}
        onClose={closeSheet}
      >
        <ProfileFields
          initialName={person.display_name}
          initialVoicePart={person.voice_part}
          initialIsBrother={person.is_brother}
          busy={busy}
          onSave={(name, voicePart, isBrother) =>
            void act(() =>
              api.updateMemberProfile(
                person.member_id,
                name,
                voicePart,
                { eyes: person.avatar_eyes, color: person.avatar_color },
                person.birthday,
                isBrother,
              ),
            ).then((updated) => {
              if (updated !== null) closeSheet();
            })
          }
        />
        {failure !== null && failure.at === 'sheet' && <p className="failure">{failure.text}</p>}
      </Sheet>

      {/* The one thing on this screen that is asked about, and for the reason
          the rule of 2026-08-21 gives: nothing here takes it back. After this
          the roster is the other person's, and only they can hand it back — so
          the screen leaves for «Хор», where the mark is already under another
          name. */}
      <Ask
        open={asking}
        title={`Передать старшинство · ${person.display_name}?`}
        cost={
          <>
            {person.display_name} сможет назначать регентов, ставить на паузу и поручать функции.
            Ты останешься регентом: спевки, участия и программа при тебе. Вернуть старшинство
            сможет только {person.display_name}.
          </>
        }
        verb="Передать старшинство"
        busy={busy}
        failure={failure !== null && failure.at === 'sheet' ? failure.text : null}
        onConfirm={() => {
          setBusy(true);
          setFailure(null);
          void api
            .handOverSeniority(person.member_id)
            .then(() => {
              setAsking(false);
              // No bar: the bar is for what can be put back. And no staying
              // here either — the buttons under this one are the head's, and
              // the reader has just stopped being the head.
              onBack();
            })
            .catch((error: unknown) => {
              setFailure({
                at: 'sheet',
                text: error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
              });
            })
            .finally(() => setBusy(false));
        }}
        onClose={() => setAsking(false)}
      />

      <PauseSheet
        person={person}
        open={sheet === 'pause'}
        note={note}
        busy={busy}
        failure={failure !== null && failure.at === 'sheet' ? failure.text : null}
        onNote={setNote}
        onClose={closeSheet}
        onSave={() =>
          void act(() => api.pauseMember(person.member_id, note.trim() || null)).then((updated) => {
            if (updated === null) return;
            closeSheet();
          })
        }
      />

      <DeleteSheet
        person={person}
        total={card.total}
        open={sheet === 'delete'}
        typed={typed}
        busy={busy}
        failure={failure !== null && failure.at === 'sheet' ? failure.text : null}
        onTyped={setTyped}
        onClose={closeSheet}
        onDelete={() => {
          setBusy(true);
          setFailure(null);
          void api
            .deleteMember(person.member_id, typed.trim())
            .then((link) => onGone(link.invite_code))
            .catch((error: unknown) => {
              setFailure({
                at: 'sheet',
                text: error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
              });
            })
            .finally(() => setBusy(false));
        }}
      />
    </>
  );
}

// (Ours.) The old `subLine` (`:561-571`), renamed `cardLine` and nothing else: the roster's
// `rosterLine` (`MembersCard.tsx`) is the other `subLine` of the old app, and it says different
// things about the same person — `FND-509`, `KEEP`, «Переносим обе под разными именами;
// объединение изменило бы обе строки». No tenure here, as in the old card: `FND-508`, `KEEP`.
/** The line under the name: the part, plus whatever else is true of the person. */
function cardLine(row: MemberRow): string {
  const parts = [voicePartName(row.voice_part)];
  if (row.is_head_regent) parts.push('старший регент');
  else if (row.role === 'regent') parts.push('регент');
  if (row.is_roster_admin) parts.push('ведёт состав');
  if (row.is_program_admin) parts.push('ведёт программу');
  if (row.is_sermon_admin) parts.push('ставит проповеди');
  if (row.is_me) parts.push('это ты');
  return parts.join(' · ');
}

/** Whether a month key falls inside one of the stretches out of the choir. */
function inAPause(month: string, pauses: PauseSpan[]): boolean {
  return pauses.some((span) => {
    const from = monthKey(span.started_at);
    const to = span.ended_at === null ? null : monthKey(span.ended_at);
    return month >= from && (to === null || month <= to);
  });
}

/** `YYYY-MM` for a moment, in the reader's own zone as the chart's keys are. */
function monthKey(iso: string): string {
  const when = new Date(iso);
  return `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * A year as twelve columns, and the gaps in it as hatching rather than as
 * nought.
 *
 * **A pause is a skip, not a zero**, which is the one thing this chart exists to
 * say. A month inside one has no rehearsals for this person at all — closing
 * writes rows only for people who were in the choir — so its share is not low,
 * it does not exist, and an empty column would read as «came to none of them».
 */
function MonthChart({ months, pauses }: { months: MemberCard['months']; pauses: PauseSpan[] }) {
  const cells = months.map((month) => ({
    ...month,
    away: month.total === 0 && inAPause(month.month, pauses),
  }));

  return (
    <>
      <div className="months">
        {cells.map((cell) => (
          <span key={cell.month} className={cell.away ? 'month hatch' : 'month'}>
            {!cell.away && cell.total > 0 && (
              <span
                className="month-fill"
                style={{ height: `${Math.round((cell.attended / cell.total) * 100)}%` }}
              />
            )}
          </span>
        ))}
      </div>
      <div className="month-names">
        {cells.map((cell) => (
          <span
            key={cell.month}
            className={cell.away ? 'month-name month-name--away' : 'month-name'}
          >
            {MONTHS_SHORT[Number(cell.month.slice(5)) - 1]}
          </span>
        ))}
      </div>
    </>
  );
}

/**
 * What the gap means, under the chart it is in.
 *
 * Three sentences and not one: a pause that is standing, a pause that has ended,
 * and a year with no gap at all. The middle one is the reason the history is a
 * table rather than a flag — somebody who came back still has the stretch drawn
 * behind them, and the line says why the percentage does not count it.
 */
function gapLine(card: MemberCard): string | null {
  const standing = card.pauses.find((span) => span.ended_at === null);
  if (standing !== undefined) {
    return (
      `В паузе с ${monthOf(new Date(standing.started_at).getMonth() + 1)}. ` +
      'Время паузы в проценте не участвует — в нём просто нет спевок.'
    );
  }
  const last = card.pauses[card.pauses.length - 1];
  // Nothing at all where nothing was interrupted: the line is about a gap, and
  // a year with no gap in it has no sentence to carry.
  if (last === undefined || last.ended_at === null) return null;
  const from = new Date(last.started_at);
  const to = new Date(last.ended_at);
  const span =
    from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()
      ? `пауза в ${monthIn(from.getMonth() + 1)}`
      : `пауза с ${MONTHS_SHORT[from.getMonth()]} по ${MONTHS_SHORT[to.getMonth()]}`;
  return `Разрыв — ${span}. Проценты считаются по месяцам в составе.`;
}

/**
 * «Поставить на паузу»: a warning where there is one, a note, and the price.
 *
 * A sheet rather than an `Ask` because there is a field in it, which is the
 * rule every form in this app follows — a field means the keyboard, and the
 * keyboard means a screen rather than a card inside a list.
 */
function PauseSheet({
  person,
  open,
  note,
  busy,
  failure,
  onNote,
  onClose,
  onSave,
}: {
  person: MemberRow;
  open: boolean;
  note: string;
  busy: boolean;
  failure: string | null;
  onNote: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const holds = person.is_program_admin
    ? 'программу'
    : person.is_sermon_admin
      ? 'назначение проповедей'
      : null;

  return (
    <Sheet
      open={open}
      label="Поставить на паузу"
      onClose={onClose}
      foot={
        <>
          {failure !== null && <p className="failure">{failure}</p>}
          <button className="btn btn--primary" disabled={busy} onClick={onSave}>
            Поставить на паузу
          </button>
        </>
      }
    >
      <p className="card-title">{person.display_name}</p>

      {/* Said before the tap rather than refused after it: a function does not
          block a pause, it comes off with the person and falls back to the
          regent — which is «не поручил — делает регент» doing its job. */}
      {holds !== null && (
        <section className="card card--toned">
          <span className="label label--tone">Функция вернётся к регенту</span>
          <p className="card-line">
            {person.display_name.split(' ')[0]} ведёт {holds}. Пауза это снимет, и функция
            вернётся к регенту — пока её не поручат другому.
          </p>
        </section>
      )}

      <label className="field">
        <span>Почему — для себя и заместителей</span>
        <textarea
          rows={2}
          maxLength={200}
          value={note}
          placeholder="Уехала учиться, вернётся к лету"
          onChange={(event) => onNote(event.target.value)}
        />
      </label>

      {/* Neutral in gender throughout: the choir has brothers and sisters in it,
          and «он» inside the confirmation of an irreversible act reads as
          somebody else's text. */}
      <p className="hint">
        Приложение закроется, бот писать перестанет. Из статистики имя уйдёт, отметки за прошлое
        останутся — в истории эти месяцы станут разрывом.
      </p>
    </Sheet>
  );
}

/**
 * «Удалить из хора»: what goes, and the name typed out to prove it was meant.
 *
 * The one irreversible act in the app that does **not** go through `Ask`. «Точно?»
 * checks nothing — anybody who has got this far taps it — and a name checks that
 * the person meant this person. `Ask` stays everywhere else it is.
 */
function DeleteSheet({
  person,
  total,
  open,
  typed,
  busy,
  failure,
  onTyped,
  onClose,
  onDelete,
}: {
  person: MemberRow;
  /** How many rehearsals of theirs the choir is about to stop counting. */
  total: number;
  open: boolean;
  typed: string;
  busy: boolean;
  failure: string | null;
  onTyped: (value: string) => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const matched = typed.trim() === person.display_name;

  return (
    <Sheet
      open={open}
      label="Удалить из хора"
      onClose={onClose}
      foot={
        <>
          {failure !== null && <p className="failure">{failure}</p>}
          {/* Dead until the name matches, and it says so by fading rather than
              by going grey: grey in this system is what has not been chosen. */}
          <button className="btn btn--danger" disabled={busy || !matched} onClick={onDelete}>
            Удалить навсегда
          </button>
        </>
      }
    >
      <p className="card-title">{person.display_name}</p>

      <ul className="erased">
        <li>
          Отметки за {total} {plural(total, ['спевку', 'спевки', 'спевок'])}. Прошлые спевки
          пересчитаются: где было «8 из 12», станет «8 из 11».
        </li>
        <li>Номера в прошлых программах — вместе с человеком.</li>
        <li>Назначенные проповеди в расписании.</li>
        <li>Имя, партия и аватар. В истории хора не останется и следа.</li>
      </ul>

      <p className="hint">
        Это не пауза: вернуть человека и его историю будет нельзя. Ссылка хора сменится, старую
        придётся заменить у всех.
      </p>

      <label className="field">
        <span>Впиши имя целиком, чтобы подтвердить</span>
        <input
          value={typed}
          maxLength={100}
          placeholder={person.display_name}
          onChange={(event) => onTyped(event.target.value)}
        />
      </label>

    </Sheet>
  );
}
